// TODO: discuss what these thresholds should be
export const DATA_LOAD_THRESHOLDS = {
  WARNING: 2 * 1024 * 1024, // 2 MB
  CRITICAL: 10 * 1024 * 1024, // 10 MB
};

export const calculateGeoJSONSize = data => {
  if (!data) return 0;

  try {
    const jsonString = JSON.stringify(data);
    return new Blob([jsonString]).size;
  } catch (error) {
    console.error('Error calculating GeoJSON size:', error);
    return 0;
  }
};

export const calculateImageSize = data => {
  if (!data) return 0;

  if (typeof data === 'string' && data.startsWith('data:image')) {
    const base64Length = data.split(',')[1]?.length || 0;
    return (base64Length * 3) / 4;
  }

  if (data instanceof Blob) {
    return data.size;
  }

  if (data.image && typeof data.image === 'string' && data.image.startsWith('data:image')) {
    const base64Length = data.image.split(',')[1]?.length || 0;
    return (base64Length * 3) / 4;
  }

  return 0;
};

// Returns size in bytes
export const calculateVectorTileSize = data => {
  if (!data) return 0;

  return new Blob([JSON.stringify(data)]).size;
};

export const estimateRasterTileLoad = (layerId, zoom = 10) => {
  // Raster tiles are 256x256 PNG images
  // Our source data has 10m x 10m resolution

  // Base tile size estimates by zoom level (accounting for 10m resolution)
  let baseTileSize;
  if (zoom <= 11) {
    baseTileSize = 15 * 1024; // 15 KB - high compression due to repeated values
  } else if (zoom <= 13) {
    baseTileSize = 20 * 1024; // 20 KB - good compression
  } else if (zoom <= 15) {
    baseTileSize = 25 * 1024; // 25 KB - moderate compression
  } else {
    baseTileSize = 30 * 1024; // 30 KB - less compression due to interpolation
  }

  // Flood depth layers have more variation in values (gradients)
  const isFloodDepth = layerId.includes('bleDep') || layerId.includes('wse');
  const multiplier = isFloodDepth ? 1.3 : 1.0;

  return baseTileSize * multiplier;
};

export const calculateLayerDataLoad = (layerId, data, config, zoom = 10) => {
  if (!data) return { size: 0, complexity: 0, type: 'unknown' };

  const mapType = config?.mapType;
  let size = 0;
  let type = 'unknown';

  if (mapType === 'geojson') {
    size = calculateGeoJSONSize(data);
    type = 'geojson';
  } else if (mapType === 'imageOverlay') {
    // Check if data is just a boolean placeholder (for tile-based layers)
    if (typeof data === 'boolean') {
      // Estimate based on typical raster tile sizes and zoom level
      size = estimateRasterTileLoad(layerId, zoom);
      type = 'raster-tiles';
    } else if (typeof data === 'object' && !Array.isArray(data)) {
      // Handle actual image data objects (e.g., combinedSurge with multiple categories)
      Object.values(data).forEach(categoryData => {
        if (categoryData?.image) {
          size += calculateImageSize(categoryData);
        }
      });
      type = 'raster';
    } else {
      // Handle single image data
      size = calculateImageSize(data);
      type = 'raster';
    }
  } else if (mapType === 'vectorTile') {
    size = calculateVectorTileSize(data);
    type = 'vectorTile';
  }

  return { size, type };
};

export const calculateTotalDataLoad = (layers, layerVisibility, layerConfigs, zoom = 10) => {
  let totalSize = 0;
  const layerBreakdown = {};

  Object.entries(layerVisibility).forEach(([layerId, isVisible]) => {
    if (isVisible && layers[layerId]) {
      const config = layerConfigs[layerId];
      const loadInfo = calculateLayerDataLoad(layerId, layers[layerId], config, zoom);

      totalSize += loadInfo.size;
      layerBreakdown[layerId] = loadInfo;
    }
  });

  return {
    totalSize,
    layerBreakdown,
    visibleLayerCount: Object.values(layerVisibility).filter(v => v).length,
  };
};

export const formatBytes = bytes => {
  if (bytes === 0) return '0 Bytes';

  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));

  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

export const getLoadStatus = totalSize => {
  if (totalSize >= DATA_LOAD_THRESHOLDS.CRITICAL) {
    return {
      level: 'high',
      message: 'Disable layers you are not actively using to improve performance',
      color: 'red',
    };
  } else if (totalSize >= DATA_LOAD_THRESHOLDS.WARNING) {
    return {
      level: 'medium',
      message: 'Disable layers you are not actively using to improve performance',
      color: 'orange',
    };
  }

  return {
    level: 'normal',
    message: 'Data load is within normal range.',
    color: 'green',
  };
};
